/**
 * ParcaTakipService — Parça Takip verisini SUNUCUDA (hareketler koleksiyonu) tutar.
 * Parça = ANA KART (turu KART, uygulama 'parca-takip'). Aksiyon = ALT KART (_uid = parçaId).
 * Paylaşım formatına (IKart) birebir uyar — feed/yorum/bildirim bedavaya gelir.
 */
import { Injectable, inject, signal } from '@angular/core';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { FirmaService } from '@/src/app/uygulamalar/firma-yonetim/services/firma.service';
import { FikoYTId } from '@/src/service/fikoyt-ui/helpers/FikoYTId';
import { IKart, IKartTuru, IEkTuru } from '@/src/service/fikoyt-ui/interfaces/IKart.interface';
import {
  HAREKETLER, PARCA_KART_BILESEN, ALT_TIP_BILGI, ASAMA_BILGI,
  IParcaVeri, IParcaFingerprint, ParcaAltTip, ParcaAsama,
} from '../interfaces/parca-takip.interface';

const VT = 'FikoYT.sistem.hareketler';
const YOL = 'FikoYT.sistem.hareketler.kalem';
const UYGULAMA = 'parca-takip';

@Injectable({ providedIn: 'root' })
export class ParcaTakipService {
  private FikoYT = inject(FikoYTService);
  private req = inject(FikoYTRequestService);
  private firma = inject(FirmaService);

  yukleniyor = signal(false);

  private aktifId(): string { return this.FikoYT.kimlik?.aktifKullaniciJson?.()?._id || ''; }
  private aktifIsim(): string { const u = this.FikoYT.kimlik?.aktifKullaniciJson?.(); return (u?.isim ? `${u.isim}${u.soyIsim ? ' ' + u.soyIsim : ''}` : u?.kullaniciAdi) || ''; }

  /** Aktif kullanıcının ilk firma yetki kaydı (firmaId + rol + yetkiler) — bir kez çözülüp önbelleğe alınır. null = firmasız. */
  private _firmaYetki: any = undefined;
  private async firmaYetkiCoz(): Promise<any> {
    if (this._firmaYetki !== undefined) return this._firmaYetki;
    const uid = this.aktifId();
    if (!uid) return (this._firmaYetki = null);
    try { const y = await this.firma.kullaniciFirmaYetkileri(uid); this._firmaYetki = y?.[0] || null; }
    catch { this._firmaYetki = null; }
    return this._firmaYetki;
  }
  private async firmaIdAl(): Promise<string | null> { return (await this.firmaYetkiCoz())?.firmaId || null; }

  /** Parça takip yetki seviyesi: 'yonet' (tam) | 'goruntule' (salt okunur) | 'yok' (erişim yok). */
  async yetkiSeviye(): Promise<'yonet' | 'goruntule' | 'yok'> {
    const u = this.FikoYT.kimlik?.aktifKullaniciJson?.();
    if ((u?.yetkiler || []).includes('Süper Yönetici')) return 'yonet';
    const y = await this.firmaYetkiCoz();
    if (!y) return 'yonet';                          // firmasız → kişisel veri (kendi parçaları)
    if (y.rol === 'firma_yoneticisi') return 'yonet'; // yönetici daima tam
    const ys: string[] = y.yetkiler || [];
    if (ys.includes('parca_takip.yonet')) return 'yonet';
    if (ys.includes('parca_takip.goruntule')) return 'goruntule';
    if (!ys.some(k => k.startsWith('parca_takip.'))) return 'goruntule';  // eski kayıt (geriye dönük) → kilitleme
    return 'yok';
  }
  private async yazabilir(): Promise<boolean> { return (await this.yetkiSeviye()) === 'yonet'; }
  /** Sorguya firma izolasyonu uygula: firma varsa firmaId, yoksa sahip bazlı. */
  private async scopeEkle(match: any): Promise<any> {
    const fid = await this.firmaIdAl();
    if (fid) match['veri.firmaId'] = fid; else match.sahip = this.aktifId();
    return match;
  }

  /** FOTO/DOSYA alt-kartı + dosya (multipart hareketler.ekle; paylaşım sistemi pattern'i). Döner: dosyaId. */
  private async _dosyaliKartEkle(parcaId: string, dosya: File, altTip: ParcaAltTip, turu: IEkTuru): Promise<string | null> {
    if (!await this.yazabilir()) return null;
    const firmaId = await this.firmaIdAl();
    const bilgi = ALT_TIP_BILGI[altTip];
    const id = new FikoYTId().toString();
    const ekId = new FikoYTId().toString();
    const ek: any = {
      _id: ekId, turu, ismi: dosya.name, tipi: dosya.type, boyut: dosya.size,
      _secilenSetId: bilgi.id, bilesen: bilgi.id, uygulama: UYGULAMA, altTip, dosyaId: ekId, sahipIsim: this.aktifIsim(),
    };
    const kart: any = {
      _id: id, _uid: parcaId, turu: IKartTuru.KART, vt: VT, yol: YOL,
      sahip: this.aktifId(), gizlilik: 'Sadece Ben', uygulama: [UYGULAMA],
      ...(firmaId ? { firmaId } : {}),
      icerikler: [{ _id: new FikoYTId().toString(), html: '', ek }],
      veri: { altTip, ...(firmaId ? { firmaId } : {}), dosyaId: ekId, ismi: dosya.name, boyut: dosya.size } as any,
      tarih: new Date(),
    };
    const fd = new FormData();
    fd.append('req_body', JSON.stringify(kart));
    fd.append(ekId, dosya, dosya.name);
    const r = await fetch(`https://${location.hostname}:9000/fapiv1/fikoyt.com.FikoYT.sistem.DB.hareketler.ekle`, { method: 'POST', body: fd, credentials: 'include' });
    return r.ok ? ekId : null;
  }
  fotoEkle(parcaId: string, dosya: File): Promise<string | null> { return this._dosyaliKartEkle(parcaId, dosya, 'FOTO', IEkTuru.RESIM); }
  dosyaEkle(parcaId: string, dosya: File): Promise<string | null> { return this._dosyaliKartEkle(parcaId, dosya, 'DOSYA', IEkTuru.DOSYA); }
  /** go_files resim görüntüleme URL'i. */
  fotoUrl(dosyaId: string, boyut = '512x512'): string {
    return `https://${location.hostname}:9000/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.${dosyaId}.resim?boyut=${boyut}&thumbnail=true`;
  }
  /** go_files ham dosya indirme URL'i (STP, pdf, vb.). */
  dosyaUrl(dosyaId: string): string {
    return `https://${location.hostname}:9000/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.${dosyaId}.dosya`;
  }

  /** STP geometrisinden kararlı parmak izi üret (dosya tekrar açılınca tanıma için). */
  fingerprintUret(faceSayisi: number, edgeSayisi: number, hacim: number, bbox?: { x: number; y: number; z: number }): IParcaFingerprint {
    const v = Math.round((hacim || 0) * 100) / 100;
    return { fp: `f${faceSayisi || 0}_e${edgeSayisi || 0}_v${v}`, faceSayisi: faceSayisi || 0, edgeSayisi: edgeSayisi || 0, hacim: v, bbox };
  }

  /** Parça veri → dinamik kart için flat ek alanları. */
  private ekVeri(v: IParcaVeri): any {
    return {
      turu: IEkTuru.UYGULAMAICERIK, _secilenSetId: PARCA_KART_BILESEN, bilesen: PARCA_KART_BILESEN, uygulama: UYGULAMA,
      parcaAdi: v.parcaAdi, revizyon: v.revizyon || '', malzeme: v.malzeme || '',
      asama: v.asama, teorikAgirlik: v.teorikAgirlik ?? '', olculenAgirlik: v.olculenAgirlik ?? '', hedefTarih: v.hedefTarih || '',
      oncelik: v.oncelik || 'normal', adet: v.adet ?? '', musteriAdi: v.musteriAdi || '',
    };
  }

  /** Yeni parça ana kartı oluştur (sunucuda). Döner: kart _id. */
  async parcaOlustur(v: IParcaVeri): Promise<string> {
    if (!await this.yazabilir()) return '';
    const id = new FikoYTId().toString();
    const firmaId = await this.firmaIdAl();
    v = { ...v, kok: true, ...(firmaId ? { firmaId } : {}) };   // ana (kök) kart + firma damgası
    const kart: IKart = {
      _id: id, turu: IKartTuru.KART, vt: VT, yol: YOL,
      sahip: this.aktifId(), gizlilik: 'Sadece Ben',
      ...(firmaId ? { firmaId } as any : {}),
      uygulama: [UYGULAMA],
      icerikler: [{ _id: new FikoYTId().toString(), ek: this.ekVeri(v) }],
      veri: v as any,
      tarih: new Date(),
    };
    await this.req.ekle(HAREKETLER.ekle, kart);
    return id;
  }

  /** Parça verisini güncelle (dot-notation $set). */
  async parcaGuncelle(parcaId: string, v: IParcaVeri): Promise<void> {
    if (!await this.yazabilir()) return;
    await this.req.guncelle(HAREKETLER.guncelle, { _id: parcaId }, {
      veri: v, 'icerikler.0.ek': this.ekVeri(v), duzenlenmeTarihi: new Date(),
    });
  }

  /** Bir alt kart (aksiyon) ekle — _uid = parçaId, paylaşım formatında. */
  async altKartEkle(parcaId: string, tip: ParcaAltTip, alanlar: Record<string, any>, html = ''): Promise<string> {
    if (!await this.yazabilir()) return '';
    const bilgi = ALT_TIP_BILGI[tip];
    const id = new FikoYTId().toString();
    const firmaId = await this.firmaIdAl();
    const ek: any = { turu: IEkTuru.UYGULAMAICERIK, _secilenSetId: bilgi.id, bilesen: bilgi.id, uygulama: UYGULAMA, altTip: tip, sahipIsim: this.aktifIsim(), ...alanlar };
    const kart: IKart = {
      _id: id, _uid: parcaId, turu: IKartTuru.KART, vt: VT, yol: YOL,
      sahip: this.aktifId(), gizlilik: 'Sadece Ben', uygulama: [UYGULAMA],
      ...(firmaId ? { firmaId } as any : {}),
      icerikler: [{ _id: new FikoYTId().toString(), html, ek }],
      veri: { altTip: tip, ...(firmaId ? { firmaId } : {}), ...alanlar } as any,
      tarih: new Date(),
    };
    await this.req.ekle(HAREKETLER.ekle, kart);
    return id;
  }

  /** Parçanın alt kartları (en yeni önce). */
  async altKartlar(parcaId: string): Promise<IKart[]> {
    const match = await this.scopeEkle({ _uid: parcaId, turu: 'KART', silindi: { $ne: true } });
    return this.req.aggregate<IKart>(HAREKETLER.aggregate, [
      { $match: match },
      { $sort: { _id: -1 } },
    ]);
  }

  /** Parça ana kartlarını sayfalı getir (liste). */
  async parcaListe(sayfa = 1, limit = 20, arama = ''): Promise<{ toplam: number; liste: IKart[] }> {
    if ((await this.yetkiSeviye()) === 'yok') return { toplam: 0, liste: [] };   // yetkisiz → boş
    const match: any = await this.scopeEkle({ uygulama: UYGULAMA, turu: 'KART', silindi: { $ne: true }, 'veri.kok': true });
    if (arama) match['veri.parcaAdi'] = { $regex: arama, $options: 'i' };
    return this.req.sayfaliSorgu<IKart>(HAREKETLER.aggregate, [{ $match: match }, { $sort: { _id: -1 } }], sayfa, limit);
  }

  /** Tek parça kartını getir. */
  async parcaGetir(parcaId: string): Promise<IKart | null> {
    const match = await this.scopeEkle({ _id: parcaId });
    const r = await this.req.aggregate<IKart>(HAREKETLER.aggregate, [{ $match: match }, { $limit: 1 }]);
    return r?.[0] || null;
  }

  /** Fingerprint ile mevcut parçayı bul (dosya tanıma) — yalnız kendi firma/kapsamında. */
  async parcaBulFingerprint(fp: string): Promise<IKart | null> {
    if (!fp) return null;
    const match = await this.scopeEkle({ uygulama: UYGULAMA, turu: 'KART', 'veri.fingerprint.fp': fp, silindi: { $ne: true } });
    const r = await this.req.aggregate<IKart>(HAREKETLER.aggregate, [
      { $match: match },
      { $sort: { _id: -1 } }, { $limit: 1 },
    ]);
    return r?.[0] || null;
  }

  /** Aşama ilerlet: ana kart veri.asama güncelle + DURUM alt kartı ekle (iz). */
  async asamaIlerlet(parcaId: string, asama: ParcaAsama, aciklama = ''): Promise<void> {
    if (!await this.yazabilir()) return;
    await this.req.guncelle(HAREKETLER.guncelle, { _id: parcaId }, { 'veri.asama': asama, 'icerikler.0.ek.asama': asama, duzenlenmeTarihi: new Date() });
    await this.altKartEkle(parcaId, 'DURUM', { asama, simge: ASAMA_BILGI[asama]?.simge || '', aciklama }, aciklama);
  }

  /** Parçayı sil (yumuşak). */
  async parcaSil(parcaId: string): Promise<void> {
    if (!await this.yazabilir()) return;
    await this.req.guncelle(HAREKETLER.guncelle, { _id: parcaId }, { silindi: true });
  }

  /** Müşteri public link token'ı üret/aktive et (loginsiz durum sayfası için). Döner: token. */
  async tokenUret(parcaId: string, mevcut?: string): Promise<string> {
    if (!await this.yazabilir()) return '';
    const token = mevcut || (Date.now().toString(36) + Math.random().toString(36).slice(2, 8));
    await this.req.guncelle(HAREKETLER.guncelle, { _id: parcaId }, { 'veri.paylasimToken': token, 'veri.tokenAktif': true });
    return token;
  }

  /** LOGİNSİZ public: token ile parçanın müşteri-güvenli görünümü (hassas alan projeksiyon DIŞI). */
  async publicParcaGetir(token: string): Promise<IKart | null> {
    if (!token) return null;
    const r = await this.req.aggregate<IKart>(HAREKETLER.aggregate, [
      { $match: { uygulama: UYGULAMA, 'veri.paylasimToken': token, 'veri.tokenAktif': true, silindi: { $ne: true } } },
      { $project: { _id: 1, 'veri.parcaAdi': 1, 'veri.asama': 1, 'veri.malzeme': 1, 'veri.revizyon': 1, 'veri.teorikAgirlik': 1, 'veri.tahminiTeslim': 1, 'veri.hedefTarih': 1, tarih: 1 } },
      { $limit: 1 },
    ]);
    return r?.[0] || null;
  }

  /** LOGİNSİZ public: parçanın müşteriye gösterilecek hareketleri (durum/teklif/onay/foto/tahsilat/müşteri-kararı; iç ölçü/not hariç). */
  async publicAltlar(parcaId: string): Promise<IKart[]> {
    return this.req.aggregate<IKart>(HAREKETLER.aggregate, [
      { $match: { _uid: parcaId, turu: 'KART', silindi: { $ne: true }, 'icerikler.0.ek.altTip': { $in: ['DURUM', 'ONAY', 'TEKLIF', 'ODEME', 'FOTO', 'MONAY'] } } },
      { $sort: { _id: -1 } },
    ]);
  }

  /** LOGİNSİZ müşteri kararı (teklif onay/red) — token = yetki, firma yetkisi GEREKMEZ. Döner: başarılı mı. */
  async publicOnay(token: string, karar: 'onay' | 'red', not = ''): Promise<boolean> {
    if (!token) return false;
    const r = await this.req.aggregate<IKart>(HAREKETLER.aggregate, [
      { $match: { uygulama: UYGULAMA, 'veri.paylasimToken': token, 'veri.tokenAktif': true, silindi: { $ne: true } } },
      { $project: { _id: 1, 'veri.firmaId': 1 } }, { $limit: 1 },
    ]);
    const p = r?.[0]; if (!p?._id) return false;
    const fid = (p.veri as any)?.firmaId;
    const id = new FikoYTId().toString();
    const bilgi = ALT_TIP_BILGI['MONAY'];
    const alanlar = { sonuc: karar === 'onay' ? 'Onaylandı' : 'Reddedildi', kaynak: 'musteri', not };
    const ek: any = { turu: IEkTuru.UYGULAMAICERIK, _secilenSetId: bilgi.id, bilesen: bilgi.id, uygulama: UYGULAMA, altTip: 'MONAY', sahipIsim: 'Müşteri', ...alanlar };
    const kart: any = {
      _id: id, _uid: p._id, turu: IKartTuru.KART, vt: VT, yol: YOL,
      sahip: '', gizlilik: 'Herkes', uygulama: [UYGULAMA],
      ...(fid ? { firmaId: fid } : {}),
      icerikler: [{ _id: new FikoYTId().toString(), html: not, ek }],
      veri: { altTip: 'MONAY', ...(fid ? { firmaId: fid } : {}), ...alanlar } as any,
      tarih: new Date(),
    };
    await this.req.ekle(HAREKETLER.ekle, kart);   // GUARD YOK — müşteri eylemi, token doğrulandı
    return true;
  }
  /** LOGİNSİZ: müşteri daha önce karar vermiş mi (token ile). */
  async publicKararVar(parcaId: string): Promise<IKart | null> {
    const r = await this.req.aggregate<IKart>(HAREKETLER.aggregate, [
      { $match: { _uid: parcaId, turu: 'KART', silindi: { $ne: true }, 'icerikler.0.ek.altTip': 'MONAY' } },
      { $sort: { _id: -1 } }, { $limit: 1 },
    ]);
    return r?.[0] || null;
  }

  /** Bir hareketi (alt kart) sil (yumuşak). */
  async altKartSil(altId: string): Promise<void> {
    if (!await this.yazabilir()) return;
    await this.req.guncelle(HAREKETLER.guncelle, { _id: altId }, { silindi: true });
  }

  /** Bir hareketin alanlarını güncelle (içerik ek + veri). */
  async altKartGuncelle(altId: string, alanlar: Record<string, any>): Promise<void> {
    if (!await this.yazabilir()) return;
    const setObj: any = { duzenlenmeTarihi: new Date() };
    for (const [k, v] of Object.entries(alanlar)) { setObj[`icerikler.0.ek.${k}`] = v; setObj[`veri.${k}`] = v; }
    await this.req.guncelle(HAREKETLER.guncelle, { _id: altId }, setObj);
  }
}
